;(function () {
  'use strict'

  // Check if edit mode is enabled
  const urlParams = new URLSearchParams(window.location.search)
  const isEditMode = urlParams.has('edit') && urlParams.get('edit') === 'true'
  let dynamicConfig = { projectId: null, orgName: null }
  const getProjectId = () => dynamicConfig.projectId || urlParams.get('project') || 'Roots'
  const getOrgName = () => dynamicConfig.orgName || urlParams.get('org') || 'Brendkit'

  if (!isEditMode) return // Exit if not editing

  // API Base (public for GET, api for POST/save)
  const PUBLIC_API_BASE = 'http://localhost:3001/public/content' // Public GET
  const API_BASE = 'http://localhost:3001/api/content' // Auth POST for save
  const token = localStorage.getItem('token') // Assume logged in for editing

  // Editable selectors (expanded for new elements)
  const editableSelectors = [
    '.editable',
    '[data-editable]',
    '.editable-heading',
    '.editable-paragraph',
    '.editable-button',
    '.editable-link',
    '.editable-textarea',
    '#heroSection', // Hero bg
    '#heroOverlay', // Overlay
    '[id^="mainHeading"]', // h1 variants
    '[id^="subHeading"]', // h2 variants
    '[id^="subSubHeading"]', // h4 variants
    '[id^="paragraph"]', // p1, p2, etc.
    '[id^="longDescription"]', // Rich textarea
    '[id^="buttonText"]', // Button text
    '[id^="linkText"]', '[id^="linkHref"]', // Link
    '[id^="productsHeading"]', '[id^="servicesHeading"]', '[id^="teamHeading"]', '[id^="featuresHeading"]', '[id^="testimonialsHeading"]', '[id^="blogHeading"]', // Section h2s
    '[id^="product"]', '[id^="service"]', '[id^="team"]', '[id^="feature"]', '[id^="testimonial"]', '[id^="blog"]' // Dynamic
  ]

  // Global editor object
  window.CMSEditor = {
    reinit: initEditor,
    setConfig: function (config) {
      if (config && config.projectId && config.orgName) {
        dynamicConfig = { projectId: config.projectId, orgName: config.orgName }
        console.log('[CMSEditor] Config set:', dynamicConfig)
        initEditor()
      }
    },
    saveMedia: function(elementId, value) {
      // Save only the media URL string
      saveContent(elementId, value, true);
    }
  }

  let initInProgress = false
  let observer
  let obsTimeout = null

  async function initEditor() {
    if (initInProgress) return
    initInProgress = true

    // Temporarily stop observing DOM changes while we apply content
    if (observer) observer.disconnect()

    const projectId = getProjectId()
    const orgName = getOrgName()
    const editableElements = Array.from(document.querySelectorAll(editableSelectors.join(', ')))
    console.log(`🔄 Re-init editor for ${editableElements.length} elements in project ${projectId}, org ${orgName}`)

    const promises = editableElements.map(async (element) => {
      let elementId = element.id || element.dataset.editable || 'unnamed' // Use id or data-editable
      if (!elementId) return

      // Auto-number if duplicate (simple check)
      if (document.querySelectorAll(`[id="${elementId}"], [data-editable="${elementId}"]`).length > 1) {
        const baseId = elementId.replace(/-\d+$/, '') // Strip existing number
        let counter = 1
        while (document.querySelector(`#${baseId}-${counter}`) || document.querySelector(`[data-editable="${baseId}-${counter}"]`)) {
          counter++
        }
        elementId = `${baseId}-${counter}`
        element.id = elementId // Assign new ID
        console.log(`Assigned auto-ID: ${elementId} (duplicate detected)`)
      }

      try {
        const content = await fetchContent(elementId)
        // Only update DOM if content is different to avoid triggering mutations
        if (content != null && element.innerHTML !== content) {
          element.innerHTML = content
        }
      } catch (err) {
        console.warn(`Failed to fetch ${elementId}:`, err)
      } finally {
        addOverlay(element, elementId)
        makeEditable(element, elementId)
      }
    })

    await Promise.all(promises)

    // Re-enable observer after updates
    if (observer) observer.observe(document.body, { childList: true, subtree: true })
    initInProgress = false
  }
  

  // Fetch content from public API
  async function fetchContent(elementId) {
    try {
      const url = `${PUBLIC_API_BASE}/${getProjectId()}/${elementId}?orgName=${encodeURIComponent(getOrgName())}`
      const res = await fetch(url)
      if (res.ok) {
        const data = await res.json()
        const content = data && data.content
        return content || null
      }
      return null // 404 fallback to element.innerHTML
    } catch (err) {
      console.warn('Fetch error:', err)
      return null
    }
  }

  // Save content to API (on blur)
  async function saveContent(elementId, content, isMedia = false) {
    if (!token) {
      console.warn('No token for save - logged out?')
      return
    }

    try {
      const url = `${API_BASE}/${getProjectId()}/${elementId}`
      const body = isMedia ? { url: content } : { content };
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      })
      if (!res.ok) {
        throw new Error(`Save failed: ${res.status}`)
      }
      console.log(`💾 Saved ${elementId}`)
    } catch (err) {
      console.error('Save error:', err)
      // Optional: Show toast or revert
    }
  }

  // Add edit overlay with suggestion if duplicate
  function addOverlay(element, elementId) {
    // Remove existing
    const existingOverlay = element.querySelector('.edit-overlay')
    if (existingOverlay) existingOverlay.remove()

    const overlay = document.createElement('div')
    overlay.className = 'edit-overlay absolute top-0 right-0 bg-orange-500 text-white px-2 py-1 rounded-bl-lg text-xs opacity-0 group-hover:opacity-100 transition-opacity z-10'
    overlay.innerHTML = `
      <span>✏️</span>
      <span class="ml-1">${elementId}</span>
    `
    overlay.style.cssText = 'position: absolute; top: 0; right: 0;'
    element.classList.add('group', 'relative') // For hover
    element.appendChild(overlay)
  }

  // Make element editable
  function makeEditable(element, elementId) {
    // Avoid attaching multiple listeners when called repeatedly
    if (element.dataset.cmsEditableAttached === '1') return
    element.dataset.cmsEditableAttached = '1'

    element.contentEditable = 'true'
    element.classList.add('focus:ring-2', 'focus:ring-orange-500', 'focus:outline-none')

    // Save on blur (sanitize before sending)
    element.addEventListener('blur', () => {
      const content = getSanitizedContent(element)
      saveContent(elementId, content)
    })

    // Save on enter
    element.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        element.blur()
      }
    })
  }

  // Return only the meaningful innerHTML for storage — remove overlays and editor-only datasets
 function getSanitizedContent(element) {
  try {
    // 1. Work on a clone so we don't blink/flicker the actual UI
    const clone = element.cloneNode(true)
    
    // 2. Remove the overlay divs
    const overlays = clone.querySelectorAll('.edit-overlay')
    overlays.forEach(n => n.remove())

    // 3. Remove CMS-specific attributes from the main element and all children
    const removeCMSAttrs = (el) => {
      if (el.dataset) {
        delete el.dataset.cmsEditableAttached
        delete el.dataset.cmsHighlightTimer
      }
      el.removeAttribute('contenteditable')
      el.classList.remove(
        'focus:ring-2', 
        'focus:ring-orange-500', 
        'focus:outline-none', 
        'group', 
        'relative', 
        'cms-edit-highlight'
      )
      // If the class attribute is now empty, remove it entirely
      if (el.classList.length === 0) el.removeAttribute('class')
    }

    removeCMSAttrs(clone)
    clone.querySelectorAll('*').forEach(removeCMSAttrs)

    // 4. Return the innerHTML (the clean content)
    // .trim() handles extra whitespace/newlines injected by the overlay HTML
    return clone.innerHTML.trim() 
    
  } catch (err) {
    console.warn('Sanitize error:', err)
    return element.innerHTML
  }
}

  // Allow other apps (React) to highlight editable elements on hover
  window.addEventListener('cms:highlight', (ev) => {
    try {
      const elementId = ev && ev.detail
      if (!elementId) return
      const el = document.getElementById(elementId) || document.querySelector(`[data-editable="${elementId}"]`)
      if (!el) return
      // Add a temporary highlight (class + inline style) to indicate editability
      el.classList.add('cms-edit-highlight')
      // apply inline outline for immediate visibility (works without CSS)
      const prevOutline = el.style.outline
      el.style.outline = '3px solid rgba(255,165,0,0.9)'
      // Clear any previous timer
      if (el._cmsHighlightTimer) clearTimeout(el._cmsHighlightTimer)
      el._cmsHighlightTimer = setTimeout(() => {
        el.classList.remove('cms-edit-highlight')
        el.style.outline = prevOutline || ''
        el._cmsHighlightTimer = null
      }, 1500)
    } catch (err) {
      console.warn('cms:highlight handler error', err)
    }
  })

  // Respond to parent requests to collect editable content for saving
window.addEventListener('message', (ev) => {
  try {
    const data = ev && ev.data
    if (!data || data.type !== 'collectEditableContent') return
    const elements = Array.from(document.querySelectorAll(editableSelectors.join(', ')))
    const contents = {}
    elements.forEach((el) => {
      const id = el.id || el.dataset.editable
      // FIX: Use the sanitization function here!
      if (id) contents[id] = getSanitizedContent(el) 
    })
    // Send back to parent with project context
    window.parent.postMessage({ type: 'editableContentResponse', projectId: getProjectId(), contents }, '*')
  } catch (err) {
    console.warn('message handler error:', err)
  }
})

  // Toggle edit mode (focus/highlight)
  function toggleEditMode(element, elementId) {
    element.focus()
    element.classList.add('ring-2', 'ring-orange-500', 'bg-orange-50')
    setTimeout(() => {
      element.classList.remove('ring-2', 'ring-orange-500', 'bg-orange-50')
    }, 2000)
  }

  // Initial load
  document.addEventListener('DOMContentLoaded', () => {
    console.log(`Editor loaded for project: ${getProjectId()}, org: ${getOrgName()}. Click elements to edit.`)
    setTimeout(() => window.CMSEditor.reinit(), 500) // After dynamic loads
  })

  // Re-init on dynamic content changes
  observer = new MutationObserver((mutations) => {
    if (initInProgress) return
    const hasAddedRemoved = mutations.some((m) => (m.addedNodes && m.addedNodes.length) || (m.removedNodes && m.removedNodes.length))
    clearTimeout(obsTimeout)
    if (hasAddedRemoved) {
      obsTimeout = setTimeout(() => window.CMSEditor.reinit(), 100) // Debounce
    }
  })
  observer.observe(document.body, { childList: true, subtree: true })
})()